import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "password-change-lk-modal__hint is-error"
}
const _hoisted_2 = {
  class: "password-change-lk-modal__hint-icon",
  "aria-hidden": "true"
}
const _hoisted_3 = {
  key: 0,
  class: "password-change-lk-modal__hint-mark",
  width: "14",
  height: "14",
  viewBox: "0 0 14 14",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}
const _hoisted_4 = {
  key: 1,
  class: "password-change-lk-modal__hint-mark",
  width: "14",
  height: "14",
  viewBox: "0 0 14 14",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}
const _hoisted_5 = {
  class: "password-change-lk-modal__hint-icon",
  "aria-hidden": "true"
}
const _hoisted_6 = {
  key: 0,
  class: "password-change-lk-modal__hint-mark",
  width: "14",
  height: "14",
  viewBox: "0 0 14 14",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}
const _hoisted_7 = {
  key: 1,
  class: "password-change-lk-modal__hint-mark",
  width: "14",
  height: "14",
  viewBox: "0 0 14 14",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg"
}
const _hoisted_8 = ["disabled"]

import { computed, reactive, ref, h, watch } from 'vue';
import { inject } from 'vue';
import type { Rule } from 'ant-design-vue/es/form';

import {
  showStcErrorNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';
import { getApiUrl } from '@/js/utils/getApiUrl';
import { getBitrixSessid } from '@/js/utils/getBitrixSessid';
import { updatePasswordChangedInfo } from '@/js/utils/update-password-changed-info';

const CHANGE_PASSWORD_ENDPOINT = '/api/auth/change-password-lk/';
const OLD_PASSWORD_ERROR_MESSAGE = 'Неверный пароль';

export default /*@__PURE__*/_defineComponent({
  __name: 'password-change-modal',
  setup(__props, { expose: __expose }) {

const passwordSettingsRoot = inject<ParentNode>('passwordSettingsRoot', document);

const ICONS_BASE =
  process.env.NODE_ENV === 'development'
    ? '/assets/images/icons'
    : '/local/templates/dist/assets/images/icons';

const isOpen = ref(false);
const submitLoading = ref(false);
const oldPasswordError = ref('');
const form = reactive({
  oldPassword: '',
  password: '',
  confirmPassword: '',
});

const rules: Record<string, Rule[]> = {
  oldPassword: [{ required: true, message: 'Введите старый пароль' }],
  password: [{ required: true, message: 'Введите новый пароль' }],
  confirmPassword: [{ required: true, message: 'Подтвердите пароль' }],
};

const isStrongPassword = (value: string): boolean => {
  const hasLength = value.length >= 6 && value.length <= 10;
  const hasUppercase = /[A-Z]/.test(value);
  const hasLowercase = /[a-z]/.test(value);
  const hasDigit = /\d/.test(value);
  return hasLength && hasUppercase && hasLowercase && hasDigit;
};

const isPasswordStrong = computed(() => isStrongPassword(form.password));
const isConfirmPasswordStrong = computed(() => isStrongPassword(form.confirmPassword));

const showPasswordStrengthHint = computed(() => form.password.trim().length > 0);
const showPasswordsMatchHint = computed(
  () => form.password.trim().length > 0 && form.confirmPassword.trim().length > 0,
);

const isPasswordsMatch = computed(
  () => showPasswordsMatchHint.value && form.password === form.confirmPassword,
);

const passwordStrengthText = computed(() =>
  isPasswordStrong.value ? 'Сильный пароль' : 'Слабый пароль',
);
const passwordsMatchText = computed(() =>
  isPasswordsMatch.value ? 'Пароли совпадают' : 'Пароли не совпадают',
);

const passwordStrengthClass = computed(() => (isPasswordStrong.value ? 'is-success' : 'is-error'));
const passwordsMatchClass = computed(() => (isPasswordsMatch.value ? 'is-success' : 'is-error'));

const canSubmit = computed(
  () =>
    !oldPasswordError.value &&
    form.oldPassword.trim().length > 0 &&
    isPasswordStrong.value &&
    isConfirmPasswordStrong.value &&
    isPasswordsMatch.value,
);

const isWrongOldPasswordError = (message: string): boolean => {
  const normalized = message.toLowerCase();

  return (
    normalized.includes('текущий пароль') ||
    normalized.includes('неверн') ||
    normalized.includes('не верен')
  );
};

const clearOldPasswordError = (): void => {
  oldPasswordError.value = '';
};

watch(
  () => form.oldPassword,
  () => {
    clearOldPasswordError();
  },
);

const readApiErrorMessage = (data: unknown): string | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }
  const record = data as Record<string, unknown>;
  const candidates = ['message', 'detail', 'error', 'msg'];
  for (const key of candidates) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  if (Array.isArray(record.detail) && record.detail.every((item) => typeof item === 'string')) {
    return record.detail.filter(Boolean).join(' ');
  }
  return null;
};

const parseResponseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) {
    return {};
  }
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
};

const onSubmit = async () => {
  clearOldPasswordError();
  submitLoading.value = true;
  try {
    const sessid = getBitrixSessid();
    if (!sessid) {
      throw new Error('Не удалось получить CSRF-токен. Обновите страницу и попробуйте снова.');
    }

    const response = await fetch(getApiUrl(CHANGE_PASSWORD_ENDPOINT), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Bitrix-Csrf-Token': sessid },
      credentials: 'same-origin',
      body: JSON.stringify({
        old_password: form.oldPassword,
        password: form.password,
        confirm_password: form.confirmPassword,
        sessid: sessid,
      }),
    });

    const data = await parseResponseBody(response);

    if (!response.ok) {
      const fromBody = readApiErrorMessage(data);
      throw new Error(fromBody ?? `Ошибка смены пароля (${response.status})`);
    }

    const record = data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
    const msg = record?.msg;
    showStcSuccessNotification(
      typeof msg === 'string' && msg.trim() ? msg.trim() : 'Пароль успешно изменён',
    );
    updatePasswordChangedInfo(undefined, passwordSettingsRoot);
    resetFormAndHideMainModal();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Не удалось сохранить пароль';

    if (isWrongOldPasswordError(message)) {
      oldPasswordError.value = OLD_PASSWORD_ERROR_MESSAGE;
      return;
    }

    showStcErrorNotification(message);
  } finally {
    submitLoading.value = false;
  }
};

const resetFormAndHideMainModal = () => {
  form.oldPassword = '';
  form.password = '';
  form.confirmPassword = '';
  clearOldPasswordError();
  isOpen.value = false;
};

const open = () => {
  isOpen.value = true;
};

const close = () => {
  resetFormAndHideMainModal();
};

const renderPasswordIcon = (visible: boolean) =>
  h('img', {
    src: visible ? `${ICONS_BASE}/share-icons/eye-slash.svg` : `${ICONS_BASE}/share-icons/eye.svg`,
    alt: '',
    width: 24,
    height: 24,
  });

__expose({ open, close });

return (_ctx: any,_cache: any) => {
  const _component_a_input_password = _resolveComponent("a-input-password")!
  const _component_a_form_item = _resolveComponent("a-form-item")!
  const _component_a_form = _resolveComponent("a-form")!
  const _component_a_modal = _resolveComponent("a-modal")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("button", {
      type: "button",
      class: "btn btn-secondary btn-medium",
      "aria-label": "Изменить пароль",
      onClick: open
    }, " Изменить пароль "),
    _createVNode(_component_a_modal, {
      open: isOpen.value,
      "onUpdate:open": _cache[3] || (_cache[3] = ($event: any) => ((isOpen).value = $event)),
      class: "password-change-lk-modal",
      footer: null,
      closable: true,
      "mask-closable": false,
      keyboard: false,
      onCancel: close,
      centered: "",
      width: "456"
    }, {
      default: _withCtx(() => [
        _cache[9] || (_cache[9] = _createElementVNode("div", { class: "password-change-lk-modal__header" }, [
          _createElementVNode("h3", { class: "password-change-lk-modal__title" }, "Изменить пароль"),
          _createElementVNode("p", { class: "password-change-lk-modal__description" }, [
            _createTextVNode(" Придумайте пароль из 6-10 символов. "),
            _createElementVNode("br"),
            _createTextVNode(" Используйте цифры, заглавные и строчные буквы. ")
          ])
        ], -1)),
        _createVNode(_component_a_form, {
          ref: "formRef",
          class: "password-change-lk-modal__form stc-ant-field-skin",
          layout: "vertical",
          "required-mark": false,
          model: form,
          rules: rules,
          onFinish: onSubmit
        }, {
          default: _withCtx(() => [
            _createVNode(_component_a_form_item, {
              name: "oldPassword",
              label: "Текущий пароль"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_a_input_password, {
                  value: form.oldPassword,
                  "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((form.oldPassword) = $event)),
                  size: "large",
                  placeholder: "Введите текущий пароль",
                  autocomplete: "current-password",
                  "icon-render": renderPasswordIcon
                }, null, 8, ["value"])
              ]),
              _: 1
            }),
            (oldPasswordError.value)
              ? (_openBlock(), _createElementBlock("p", _hoisted_1, [
                  _cache[4] || (_cache[4] = _createElementVNode("span", {
                    class: "password-change-lk-modal__hint-icon",
                    "aria-hidden": "true"
                  }, [
                    _createElementVNode("svg", {
                      class: "password-change-lk-modal__hint-mark",
                      width: "14",
                      height: "14",
                      viewBox: "0 0 14 14",
                      fill: "none",
                      xmlns: "http://www.w3.org/2000/svg"
                    }, [
                      _createElementVNode("circle", {
                        cx: "7",
                        cy: "7",
                        r: "7",
                        fill: "var(--stc-red-600)"
                      }),
                      _createElementVNode("path", {
                        d: "M4.25 4.25 L9.75 9.75 M9.75 4.25 L4.25 9.75",
                        stroke: "#fff",
                        "stroke-width": "1.35",
                        "stroke-linecap": "round"
                      })
                    ])
                  ], -1)),
                  _createElementVNode("span", null, _toDisplayString(oldPasswordError.value), 1)
                ]))
              : _createCommentVNode("", true),
            _createVNode(_component_a_form_item, {
              name: "password",
              label: "Новый пароль"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_a_input_password, {
                  value: form.password,
                  "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((form.password) = $event)),
                  size: "large",
                  placeholder: "Введите новый пароль",
                  autocomplete: "new-password",
                  "icon-render": renderPasswordIcon
                }, null, 8, ["value"])
              ]),
              _: 1
            }),
            (showPasswordStrengthHint.value)
              ? (_openBlock(), _createElementBlock("p", {
                  key: 1,
                  class: _normalizeClass(["password-change-lk-modal__hint", passwordStrengthClass.value])
                }, [
                  _createElementVNode("span", _hoisted_2, [
                    (isPasswordStrong.value)
                      ? (_openBlock(), _createElementBlock("svg", _hoisted_3, [...(_cache[5] || (_cache[5] = [
                          _createElementVNode("circle", {
                            cx: "7",
                            cy: "7",
                            r: "7",
                            fill: "#52c41a"
                          }, null, -1),
                          _createElementVNode("path", {
                            d: "M3.5 7 L6 9.5 L10.5 4",
                            stroke: "#fff",
                            "stroke-width": "1.5",
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round"
                          }, null, -1)
                        ]))]))
                      : (_openBlock(), _createElementBlock("svg", _hoisted_4, [...(_cache[6] || (_cache[6] = [
                          _createElementVNode("circle", {
                            cx: "7",
                            cy: "7",
                            r: "7",
                            fill: "var(--stc-red-600)"
                          }, null, -1),
                          _createElementVNode("path", {
                            d: "M4.25 4.25 L9.75 9.75 M9.75 4.25 L4.25 9.75",
                            stroke: "#fff",
                            "stroke-width": "1.35",
                            "stroke-linecap": "round"
                          }, null, -1)
                        ]))]))
                  ]),
                  _createElementVNode("span", null, _toDisplayString(passwordStrengthText.value), 1)
                ], 2))
              : _createCommentVNode("", true),
            _createVNode(_component_a_form_item, {
              name: "confirmPassword",
              label: "Подтвердить пароль"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_a_input_password, {
                  value: form.confirmPassword,
                  "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((form.confirmPassword) = $event)),
                  size: "large",
                  placeholder: "Подтвердите пароль",
                  autocomplete: "new-password",
                  "icon-render": renderPasswordIcon
                }, null, 8, ["value"])
              ]),
              _: 1
            }),
            (showPasswordsMatchHint.value)
              ? (_openBlock(), _createElementBlock("p", {
                  key: 2,
                  class: _normalizeClass(["password-change-lk-modal__hint", passwordsMatchClass.value])
                }, [
                  _createElementVNode("span", _hoisted_5, [
                    (isPasswordsMatch.value)
                      ? (_openBlock(), _createElementBlock("svg", _hoisted_6, [...(_cache[7] || (_cache[7] = [
                          _createElementVNode("circle", {
                            cx: "7",
                            cy: "7",
                            r: "7",
                            fill: "#52c41a"
                          }, null, -1),
                          _createElementVNode("path", {
                            d: "M3.5 7 L6 9.5 L10.5 4",
                            stroke: "#fff",
                            "stroke-width": "1.5",
                            "stroke-linecap": "round",
                            "stroke-linejoin": "round"
                          }, null, -1)
                        ]))]))
                      : (_openBlock(), _createElementBlock("svg", _hoisted_7, [...(_cache[8] || (_cache[8] = [
                          _createElementVNode("circle", {
                            cx: "7",
                            cy: "7",
                            r: "7",
                            fill: "var(--stc-red-600)"
                          }, null, -1),
                          _createElementVNode("path", {
                            d: "M4.25 4.25 L9.75 9.75 M9.75 4.25 L4.25 9.75",
                            stroke: "#fff",
                            "stroke-width": "1.35",
                            "stroke-linecap": "round"
                          }, null, -1)
                        ]))]))
                  ]),
                  _createElementVNode("span", null, _toDisplayString(passwordsMatchText.value), 1)
                ], 2))
              : _createCommentVNode("", true),
            _createElementVNode("button", {
              type: "submit",
              class: "password-change-lk-modal__submit btn btn-primary",
              disabled: !canSubmit.value || submitLoading.value
            }, " Сохранить ", 8, _hoisted_8)
          ]),
          _: 1
        }, 8, ["model"])
      ]),
      _: 1
    }, 8, ["open"])
  ], 64))
}
}

})